import type { MessageKey } from '@/shared/i18n/messages';
import type { Translate } from '@/shared/i18n/I18nProvider';

/**
 * The people ladder. Each rung contains the ones below it — except `developer`,
 * which is deliberately NOT on it (CLAUDE.md, multitenancy).
 */
export type OrgRole = 'owner' | 'admin' | 'coordinator' | 'member' | 'viewer' | 'developer' | 'designer';

export type OrgKind = 'personal' | 'company';

export interface Organization {
  readonly id: string;
  /** Internal and never shown: derived at signup, or from the name with a
   *  random tail. Not the address in the URL — that is `publicSlug`. */
  readonly slug: string;
  /**
   * The vanity address: numtable.com/<publicSlug>. Null until a slug request
   * has been approved, which is the normal state — an organization is fully
   * usable without one.
   */
  readonly publicSlug: string | null;
  readonly name: string;
  readonly kind: OrgKind;
  /**
   * The one organization that runs Numtable itself. It is an ordinary tenant
   * with a flag, so roles, invitations and the switcher all work inside it
   * unchanged.
   *
   * Reading it here only decides whether the back-office appears in the menu.
   * The authority is the Worker, which re-checks membership and capability with
   * `service_role` on every `/api/admin/*` call.
   */
  readonly isPlatform: boolean;
}

export interface Membership {
  readonly id: string;
  readonly orgId: string;
  /** The person. Distinct from `id`, which is the membership row. */
  readonly userId: string;
  readonly role: OrgRole;
  readonly teamId: string | null;
  readonly organization: Organization;
}

/**
 * Authority over people, and nothing else. `developer` and `designer` rank 0 on
 * purpose: they grant technical/design capabilities, so answering a capability
 * question with this would be exactly the confusion the schema is built to prevent.
 */
const RANK: Record<OrgRole, number> = {
  owner: 50,
  admin: 40,
  coordinator: 30,
  member: 20,
  viewer: 10,
  developer: 0,
  designer: 0,
};

export const roleRank = (role: OrgRole): number => RANK[role];

/** Bypasses `module_permissions`. A coordinator does not. */
export const isAdmin = (role: OrgRole): boolean =>
  role === 'owner' || role === 'admin';

export const isOwner = (role: OrgRole): boolean => role === 'owner';

/**
 * Whether this role may edit the organization's theme colours.
 *
 * A capability, not a rung: `developer` and `designer` rank 0 in `RANK` on purpose.
 */
export const canEditTheme = (role: OrgRole): boolean =>
  role === 'owner' || role === 'developer' || role === 'designer';

/**
 * Whether `actor` may manage `target` on the ladder.
 */
export function canManage(
  actor: { role: OrgRole; teamId: string | null },
  target: { role: OrgRole; teamId: string | null },
): boolean {
  if (
    actor.role === 'developer' ||
    target.role === 'developer' ||
    actor.role === 'designer' ||
    target.role === 'designer'
  ) {
    return false;
  }
  if (isAdmin(actor.role)) return roleRank(actor.role) > roleRank(target.role);
  if (actor.role === 'coordinator') {
    return (
      target.role === 'member' &&
      actor.teamId !== null &&
      actor.teamId === target.teamId
    );
  }
  return false;
}

/**
 * Returns allowed view modes for an actor based on their actual role hierarchy.
 *
 * Hierarchy (from highest to lowest):
 * 1. Owner -> Owner, Developer, Designer, User/Member, Viewer/Mirador
 * 2. Developer -> Developer, Designer, User/Member, Viewer/Mirador
 * 3. Designer -> Designer, User/Member, Viewer/Mirador
 * 4. Superior / Admin / Coordinator -> User/Member, Viewer/Mirador
 * 5. User / Member -> User/Member, Viewer/Mirador
 * 6. Viewer / Mirador -> Viewer/Mirador
 *
 * The first entry is always the actor's own role, which is what selecting it
 * clears the override with. An owner shares no entry with developer: owning and
 * previewing a developer's view are different states, and collapsing them left
 * an owner no row to return to.
 */
export function getAllowedViewModes(actualRole: OrgRole): readonly OrgRole[] {
  switch (actualRole) {
    case 'owner':
      return ['owner', 'developer', 'designer', 'member', 'viewer'];
    case 'developer':
      return ['developer', 'designer', 'member', 'viewer'];
    case 'designer':
      return ['designer', 'member', 'viewer'];
    case 'admin':
    case 'coordinator':
    case 'member':
      return ['member', 'viewer'];
    case 'viewer':
      return ['viewer'];
    default:
      return ['viewer'];
  }
}

/**
 * Validates whether the target view mode is allowed for the actual role.
 */
export function isAllowedViewMode(actualRole: OrgRole, mode: OrgRole): boolean {
  return getAllowedViewModes(actualRole).includes(mode);
}

/**
 * Resolves the display label for a view mode role using i18n translation keys.
 */
export function getViewModeLabel(
  role: OrgRole,
  t: Translate,
): string {
  switch (role) {
    case 'developer':
      return t('roles.developer');
    case 'designer':
      return t('roles.designer');
    case 'admin':
      return t('roles.admin');
    case 'coordinator':
      return t('roles.coordinator');
    case 'member':
      return t('roles.user');
    case 'viewer':
      return t('roles.viewer');
    case 'owner':
      return t('roles.owner');
    default:
      return t(`roles.${role}` as MessageKey);
  }
}

/**
 * Checks whether a view mode is currently active.
 *
 * With no override this marks the row standing for the actor's own role, which
 * `setViewMode` also treats as "stop previewing". Exactly one row per menu may
 * match: two would leave the checkmark ambiguous and make which row restores you
 * a coin flip. That is why an owner matches Owner alone and not Developer.
 */
export function isViewModeActive(
  mode: OrgRole,
  activeViewMode: OrgRole | null,
  actualRole: OrgRole,
): boolean {
  if (activeViewMode !== null) {
    return activeViewMode === mode;
  }
  if (mode === 'developer') {
    return actualRole === 'developer';
  }
  if (mode === 'member') {
    return actualRole === 'member' || actualRole === 'admin' || actualRole === 'coordinator';
  }
  return actualRole === mode;
}


